import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import FieldConfigurationAPI from "../../../../../lib/api/FieldConfiguration";
import { getActiveCompanyOptions } from "../AccessGroupsList";

export default function CopyGroupModal({
  open,
  group,
  companies,
  onClose,
  onCopied,
}) {
  const [name, setName] = useState("");
  const [targetCompanyUuid, setTargetCompanyUuid] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const targetOptions = getActiveCompanyOptions(companies);

  useEffect(() => {
    if (!open) return;
    setName(`Copy of ${group?.name || ""}`);
    setTargetCompanyUuid(
      targetOptions.some((opt) => opt.value === group?.company_uuid)
        ? group.company_uuid
        : targetOptions[0]?.value || ""
    );
    setError(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  async function handleCopy() {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Please enter a name for the copy.");
      return;
    }
    if (!targetCompanyUuid) {
      setError("Please select a company to copy this access group to.");
      return;
    }

    setIsSaving(true);
    setError(null);

    const targetCompanyName =
      targetOptions.find((opt) => opt.value === targetCompanyUuid)?.name ||
      "the selected company";

    const existingGroups = await FieldConfigurationAPI.getGroups(
      targetCompanyUuid
    );
    if (existingGroups.status !== 200) {
      setIsSaving(false);
      setError("Failed to check for existing access groups. Please try again.");
      return;
    }
    const nameConflict = (existingGroups.data || []).some(
      (g) => g.name?.trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (nameConflict) {
      setIsSaving(false);
      setError(
        `An access group named "${trimmedName}" already exists in ${targetCompanyName}. Choose a different name and try again.`
      );
      return;
    }

    const createResult = await FieldConfigurationAPI.createGroup(
      {
        name: trimmedName,
        description: group.description || "",
        access_codes: group.access_codes || [],
      },
      targetCompanyUuid
    );
    if (createResult.status !== 200 && createResult.status !== 201) {
      setIsSaving(false);
      setError(
        createResult.data?.message ||
          "Failed to create the access group in the target company."
      );
      return;
    }
    const newGroup = createResult.data;

    const filtersResult = await FieldConfigurationAPI.getGroupFilters(
      group.uuid,
      group.company_uuid
    );
    const sourceFilters = Array.isArray(filtersResult.data)
      ? filtersResult.data
      : [];

    const filterResults = await Promise.all(
      sourceFilters.map((f) =>
        FieldConfigurationAPI.upsertGroupFilter(
          newGroup.uuid,
          { resource: f.resource, mode: f.mode, rules: f.rules || [] },
          targetCompanyUuid
        )
      )
    );

    setIsSaving(false);

    const failedFilters = filterResults.some(
      (r) => r.status !== 200 && r.status !== 201
    );
    if (failedFilters) {
      setError(
        `"${trimmedName}" was created in ${targetCompanyName}, but not all permissions could be copied. Please review it there.`
      );
      return;
    }

    onCopied(newGroup, targetCompanyName);
  }

  return (
    <div className="jfm-overlay" onClick={onClose}>
      <div className="jfm-container" onClick={(e) => e.stopPropagation()}>
        <div className="jfm-header">
          <span className="jfm-title">Copy Access Group</span>
          <button className="jfm-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="jfm-body">
          <p style={{ fontSize: "13px", color: "#475569", margin: 0 }}>
            Creates a new access group with the same permissions currently set
            on <strong>"{group?.name}"</strong>.
          </p>
          <div className="jfm-field">
            <label>Name</label>
            <input
              type="text"
              value={name}
              disabled={isSaving}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="jfm-field">
            <label>Company</label>
            <select
              value={targetCompanyUuid}
              disabled={isSaving}
              onChange={(e) => setTargetCompanyUuid(e.target.value)}
            >
              {targetOptions.length === 0 && (
                <option value="">No companies available</option>
              )}
              {targetOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && <div className="jfm-error">{error}</div>}

        <div className="jfm-footer">
          <div />
          <div className="jfm-footer-actions">
            <button
              className="jfm-cancel"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              className="jfm-save"
              onClick={handleCopy}
              disabled={isSaving || targetOptions.length === 0}
            >
              {isSaving ? "Copying..." : "Copy"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
